import type { PartDefinition } from "@rogue/rackbuilder"
import { buildIndyCarriage } from "./indyCarriage"
import { getTopRailOffset, getBottomRailAnchor, getHRWeightStackPulleyOffset, getFrontTopPulleyOffset } from "./indyOffsets"
import { getDepthInInches, isHalfRack, is43Depth, getRotateYAdjustments, getFlipX, is80Height, is24Depth, is30Depth, IndyContext } from "./indyHelpers"
import { getRackModel } from "@/js/pages/product/rackbuilder/modelPaths"
import { AdjustOnlyVisual } from "@/js/pages/product/rackbuilder/constants"

// MARK: Pulley Hardware
function buildPulleyHardware(context: IndyContext): PartDefinition {
  return {
    mesh      : getRackModel('INDY', 'pulley hardware'),
    slots     : {},
    materials : context.materials,
  }
}

// MARK: Rails
export function buildTopRail(context: IndyContext): PartDefinition {
  const { config, materials, left } = context
  const depth = getDepthInInches(config)
  
  // 43" half rack has special rail
  if (isHalfRack(config) && is43Depth(config)) {
    return {
      mesh           : getRackModel('INDY', 'halfrack top rail 43'),
      slots          : {},
      anchor         : 'main',
      materials,
      offset         : { x: 0.1 },
      adjustments    : [...getRotateYAdjustments(left), ...getFlipX(left)],
      adjustmentType : is80Height(config) ? undefined : AdjustOnlyVisual,
    }
  }
  
  return {
    mesh: is80Height(config)
      ? getRackModel('INDY_80', `top rail ${depth} 80`)
      : getRackModel('INDY', `top rail ${depth}`),
    slots          : {},
    anchor         : !left && is80Height(config) ? 'right' : '',
    materials,
    offset         : getTopRailOffset(config, left),
    adjustments    : [...getRotateYAdjustments(left), ...getFlipX(left)],
    adjustmentType : is80Height(config) ? undefined : AdjustOnlyVisual,
  }
}

export function buildBottomRail(context: IndyContext): PartDefinition {
  const { config, materials, left } = context
  const depth = getDepthInInches(config)
  
  // 43" half rack has special rail
  if (isHalfRack(config) && is43Depth(config)) {
    return {
      mesh           : getRackModel('INDY', 'halfrack bottom rail 43'),
      slots          : {},
      anchor         : getBottomRailAnchor(config, left),
      offset         : { z: -2 },
      materials,
      adjustments    : [...getRotateYAdjustments(left), ...getFlipX(left)],
      adjustmentType : is80Height(config) ? undefined : AdjustOnlyVisual,
    }
  }
  
  return {
    mesh: is80Height(config)
      ? getRackModel('INDY_80', `bottom rail ${depth} 80`)
      : getRackModel('INDY', `bottom rail ${depth}`),
    slots          : {},
    anchor         : getBottomRailAnchor(config, left),
    materials,
    adjustments    : [...getRotateYAdjustments(left), ...getFlipX(left)],
    adjustmentType : is80Height(config) ? undefined : AdjustOnlyVisual,
  }
}

// MARK: Back Pulleys
export function buildTopBackPulley(context: IndyContext): PartDefinition {
  const { config, materials, left } = context
  const topBackPulleyMesh = is80Height(config)
    ? getRackModel('INDY_80', 'top back pulley 80')
    : getRackModel('INDY', 'top back pulley')

  // 43" half rack has different offset
  if (isHalfRack(config) && is43Depth(config)) {
    return {
      mesh        : topBackPulleyMesh,
      slots       : {},
      materials,
      offset      : { z: -32 },
      adjustments : getFlipX(left),
    }
  }

  return {
    mesh        : topBackPulleyMesh,
    slots       : {},
    materials,
    offset      : left ? { x: -0.3, z: -10 } : { x: 0.2, z: -10 },
    adjustments : getFlipX(left),
  }
}

export function buildBottomBackPulley(context: IndyContext): PartDefinition {
  const { config, materials, left } = context

  // 43" half rack has different mesh and offset
  if (isHalfRack(config) && is43Depth(config)) {
    return {
      mesh: is80Height(config)
        ? getRackModel('INDY_80', 'bottom back pulley 80')
        : getRackModel('INDY', 'halfrack bottom back pulley'),
      slots       : {},
      materials,
      offset      : { z: -18 },
      adjustments : getFlipX(left),
    }
  }

  return {
    mesh: is80Height(config)
      ? getRackModel('INDY_80', 'bottom back pulley 80')
      : getRackModel('INDY', 'bottom back pulley'),
    slots       : {},
    materials,
    offset      : !is80Height(config) ? { z: 1.6 } : { z: 2 },
    adjustments : getFlipX(left),
  }
}

// MARK: Top Pulleys
export function buildHalfRackTopPulley(context: IndyContext): PartDefinition {
  const { config, materials, left } = context
  const flip = getFlipX(left)

  
  
  
  const weightStackPulley: PartDefinition = {
    mesh   : getRackModel('INDY', 'weight stack pulley'),
    slots  : {},
    materials,
    offset : getHRWeightStackPulleyOffset(config),
  }
  
  // 24" depth
  if (is24Depth(config)) {
    if (is80Height(config)) {
      return {
        mesh        : getRackModel('INDY_80', 'halfrack top pulley 80'),
        slots       : { back: weightStackPulley },
        materials,
        offset      : left ? { x: 0.5, z: -2.6 } : { x: -0.5, z: -2.6 },
        adjustments : flip,
      }
    }
    
    return {
      mesh        : getRackModel('INDY', 'halfrack top pulley'),
      slots       : { back: weightStackPulley },
      materials,
      offset      : { z: -2.5 },
      adjustments : flip,
    }
  }
  
  // 30" depth
  if (is30Depth(config)) {
    return {
      mesh        : getRackModel('INDY', 'top pulley 30'),
      slots       : { back: weightStackPulley },
      materials,
      offset      : { z: 0 },
      adjustments : flip,
    }
  }
  
  // 43" depth: uses rail instead of weight stack pulley
  if (is43Depth(config)) {
    return {
      mesh        : getRackModel('INDY', 'top front pulley'),
      slots       : { rail: buildTopRail(context), hardware_43_pulley: buildPulleyHardware(context) },
      materials,
      offset      : left ? { x: 0.5 } : { x: -0.5 },
      adjustments : flip,
    }
  }
  
  // Default
  return {
    mesh        : getRackModel('INDY', 'halfrack top pulley'),
    slots       : { back: weightStackPulley },
    materials,
    adjustments : flip,
  }
}

export function buildFullRackTopPulley(context: IndyContext): PartDefinition {
  const { config, materials, left } = context
  const depth = getDepthInInches(config)

  return {
    mesh: is80Height(config)
      ? getRackModel('INDY_80', 'top front pulley 80')
      : getRackModel('INDY', 'top front pulley'),
    slots: {
      rail                         : buildTopRail(context),
      [`hardware_${depth}_pulley`] : buildPulleyHardware(context),
    },
    materials,
    offset      : getFrontTopPulleyOffset(config, left),
    adjustments : getFlipX(left),
  }
}

// MARK: Bottom Pulleys
export function buildHalfRackFrontBottomPulley(
  context: IndyContext,
  indyCarriage: PartDefinition
): PartDefinition {
  const { config, materials, left } = context
  const flip = getFlipX(left)
  
  // 24" depth
  if (is24Depth(config)) {
    if (is80Height(config)) {
      return {
        mesh        : getRackModel('INDY_80', 'halfrack bottom pulley 80'),
        slots       : { front: indyCarriage },
        materials,
        offset      : { z: 1.5 },
        adjustments : flip,
      }
    }
    
    return {
      mesh        : getRackModel('INDY', 'halfrack bottom back pulley'),
      slots       : { front: indyCarriage },
      offset      : { x: -0.1, z: 1.5 },
      materials,
      adjustments : flip,
    }
  }
  
  // 30" depth
  if (is30Depth(config)) {
    return {
      mesh        : getRackModel('INDY', 'bottom pulley 30'),
      slots       : { front: indyCarriage },
      offset      : { z: -6 },
      materials,
      adjustments : flip,
    }
  }
  
  // 43" depth
  if (is43Depth(config)) {
    return {
      mesh  : getRackModel('INDY_80', 'bottom front pulley 80'),
      slots : {
        front              : indyCarriage,
        hardware_43_pulley : buildPulleyHardware(context),
        rail               : buildBottomRail(context),
      },
      materials,
      offset      : is80Height(config) ? { z: 6 } : { z: 0 },
      adjustments : flip,
    }
  }
  
  // Default (24" non-80")
  // NOTE: 'indy bottom front pulley' retains its product prefix intentionally.
  // INDY has two distinct bottom front pulley meshes — one reused from Twin ('bottom front pulley')
  // and one built specifically for Indy ('indy bottom front pulley'). Renaming to match the
  // standard convention would collide with the Twin mesh. Do not rename.
  return {
    mesh        : getRackModel('INDY', 'indy bottom front pulley'),
    slots       : { front: indyCarriage },
    materials,
    adjustments : flip,
  }
}

export function buildFullRackFrontBottomPulley(
  context: IndyContext,
  indyCarriage: PartDefinition
): PartDefinition {
  const { config, materials, left } = context
  const depth = getDepthInInches(config)
  
  // NOTE: 'indy bottom front pulley' retains its product prefix intentionally — see comment
  // in buildHalfRackFrontBottomPulley default case for the full explanation.
  const mesh = is80Height(config)
    ? getRackModel('INDY_80', 'bottom front pulley 80')
    : getRackModel('INDY', 'indy bottom front pulley')

  return {
    mesh,
    slots: {
      front                        : indyCarriage,
      [`hardware_${depth}_pulley`] : buildPulleyHardware(context),
      rail                         : buildBottomRail(context),
    },
    materials,
    offset      : is80Height(config) ? { z: 6 } : { z: 0 },
    adjustments : getFlipX(left),
  }
}

// MARK: Front Bottom Pulley (Entry Point)
export function buildFrontBottomPulley(
  context: IndyContext, 
  trolleyClip: PartDefinition
): PartDefinition {
  const { config } = context
  const indyCarriage = buildIndyCarriage(context, trolleyClip)
  
  if (isHalfRack(config)) {
    return buildHalfRackFrontBottomPulley(context, indyCarriage)
  }
  
  return buildFullRackFrontBottomPulley(context, indyCarriage)
}